import { useState, FormEvent } from "react";

interface LeadFormSectionProps {
  formRef?: React.RefObject<HTMLDivElement>;
}

export default function LeadFormSection({ formRef }: LeadFormSectionProps) {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const textarea = form.querySelector("textarea");
    if (textarea && textarea.value.length > 500) {
      return;
    }
    setSubmitting(true);
    const data = new URLSearchParams();
    const fd = new FormData(form);
    fd.forEach((val, key) => data.append(key, val as string));
    try {
      await fetch("https://readdy.ai/api/form/d795c53ll6jbq2vddi8g", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: data.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="w-full bg-white py-28 px-6 lg:px-12">
      <div className="max-w-6xl mx-auto" ref={formRef as React.RefObject<HTMLDivElement>}>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">

          {/* Left */}
          <div className="lg:pt-4">
            <span
              className="text-stone-400 text-xs uppercase tracking-[0.28em] font-medium block mb-5"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              Free Consultation
            </span>
            <h2
              className="text-stone-900 font-semibold mb-6 leading-tight"
              style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(2.2rem, 3.5vw, 3.2rem)" }}
            >
              Start Your Project
              <br />
              <span className="font-light italic">Today</span>
            </h2>
            <p
              className="text-stone-500 text-sm leading-relaxed mb-10"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              Tell us about your project and we&apos;ll get back to you with a detailed, no-obligation quote. Serving residential and commercial clients across Ontario.
            </p>

            <div className="space-y-5">
              {[
                { icon: "ri-phone-line", label: "Phone", text: "(416) 724-1172", href: "tel:4167241172" },
                { icon: "ri-mail-line", label: "Email", text: "info@tauruscontracting.ca", href: "mailto:info@tauruscontracting.ca" },
                { icon: "ri-map-pin-line", label: "Address", text: "1355 Military Trail, Toronto, ON, M1C 1A3", href: "#" },
              ].map((item) => (
                <a key={item.text} href={item.href} className="flex items-center gap-4 group cursor-pointer">
                  <div className="w-11 h-11 flex items-center justify-center rounded-sm bg-stone-100 group-hover:bg-stone-900 transition-colors duration-300 flex-shrink-0">
                    <i className={`${item.icon} text-stone-600 group-hover:text-white transition-colors duration-300 text-base`}></i>
                  </div>
                  <div>
                    <p className="text-stone-400 text-xs uppercase tracking-wider mb-0.5" style={{ fontFamily: "'Inter', sans-serif" }}>{item.label}</p>
                    <p className="text-stone-700 text-sm font-medium group-hover:text-stone-900 transition-colors" style={{ fontFamily: "'Inter', sans-serif" }}>{item.text}</p>
                  </div>
                </a>
              ))}
            </div>

            {/* Social */}
            <div className="flex gap-3 mt-10">
              {[
                { icon: "ri-instagram-line", href: "https://www.instagram.com/tauruscontracting.ca/" },
                { icon: "ri-facebook-line", href: "https://www.facebook.com/tauruscontracting.ca/" },
                { icon: "ri-linkedin-line", href: "https://ca.linkedin.com/company/taurus-contracting-limited-a-division-of-tcl-inc/" },
              ].map((s) => (
                <a
                  key={s.href}
                  href={s.href}
                  rel="nofollow"
                  target="_blank"
                  className="w-10 h-10 flex items-center justify-center rounded-sm border border-stone-200 text-stone-500 hover:border-stone-800 hover:text-stone-900 transition-all cursor-pointer"
                >
                  <i className={`${s.icon} text-base`}></i>
                </a>
              ))}
            </div>
          </div>

          {/* Right: Form */}
          <div className="bg-stone-50 rounded-sm p-8 lg:p-10 border border-stone-100">
            {submitted ? (
              <div className="text-center py-16">
                <div className="w-16 h-16 flex items-center justify-center rounded-full bg-stone-900 mx-auto mb-5">
                  <i className="ri-check-line text-white text-2xl"></i>
                </div>
                <h3
                  className="text-stone-900 font-semibold text-2xl mb-3"
                  style={{ fontFamily: "'Cormorant Garamond', serif" }}
                >
                  Message Received
                </h3>
                <p className="text-stone-500 text-sm" style={{ fontFamily: "'Inter', sans-serif" }}>
                  We&apos;ll be in touch within 24 hours.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} data-readdy-form className="space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-stone-500 text-xs font-medium mb-2 uppercase tracking-wider" style={{ fontFamily: "'Inter', sans-serif" }}>
                      Name <span className="text-stone-400">*</span>
                    </label>
                    <input
                      name="name"
                      required
                      type="text"
                      placeholder="Your full name"
                      className="w-full px-4 py-3 border border-stone-200 rounded-sm text-sm text-stone-800 focus:outline-none focus:border-stone-500 bg-white transition-colors"
                      style={{ fontFamily: "'Inter', sans-serif" }}
                    />
                  </div>
                  <div>
                    <label className="block text-stone-500 text-xs font-medium mb-2 uppercase tracking-wider" style={{ fontFamily: "'Inter', sans-serif" }}>
                      Phone <span className="text-stone-400">*</span>
                    </label>
                    <input
                      name="phone"
                      required
                      type="tel"
                      placeholder="Your phone number"
                      className="w-full px-4 py-3 border border-stone-200 rounded-sm text-sm text-stone-800 focus:outline-none focus:border-stone-500 bg-white transition-colors"
                      style={{ fontFamily: "'Inter', sans-serif" }}
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-stone-500 text-xs font-medium mb-2 uppercase tracking-wider" style={{ fontFamily: "'Inter', sans-serif" }}>
                    Email <span className="text-stone-400">*</span>
                  </label>
                  <input
                    name="email"
                    required
                    type="email"
                    placeholder="your@email.com"
                    className="w-full px-4 py-3 border border-stone-200 rounded-sm text-sm text-stone-800 focus:outline-none focus:border-stone-500 bg-white transition-colors"
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  />
                </div>
                <div>
                  <label className="block text-stone-500 text-xs font-medium mb-2 uppercase tracking-wider" style={{ fontFamily: "'Inter', sans-serif" }}>
                    Project Type
                  </label>
                  <select
                    name="project_type"
                    className="w-full px-4 py-3 border border-stone-200 rounded-sm text-sm text-stone-800 focus:outline-none focus:border-stone-500 bg-white cursor-pointer transition-colors"
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  >
                    <option value="">Select a service</option>
                    <option value="Deck Construction">Deck Construction</option>
                    <option value="Fence Installation">Fence Installation</option>
                    <option value="Exterior Carpentry">Exterior Carpentry</option>
                    <option value="Outdoor Structures">Outdoor Structures</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="block text-stone-500 text-xs font-medium mb-2 uppercase tracking-wider" style={{ fontFamily: "'Inter', sans-serif" }}>
                    Message
                  </label>
                  <textarea
                    name="message"
                    rows={4}
                    maxLength={500}
                    placeholder="Describe your project..."
                    onChange={(e) => setCharCount(e.target.value.length)}
                    className="w-full px-4 py-3 border border-stone-200 rounded-sm text-sm text-stone-800 focus:outline-none focus:border-stone-500 bg-white resize-none transition-colors"
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  />
                  <p className="text-stone-400 text-xs mt-1 text-right" style={{ fontFamily: "'Inter', sans-serif" }}>
                    {charCount}/500
                  </p>
                </div>
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full py-4 bg-stone-900 text-white font-semibold text-sm uppercase tracking-[0.12em] rounded-sm hover:bg-stone-700 transition-all disabled:opacity-60 cursor-pointer whitespace-nowrap"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  {submitting ? "Sending..." : "Request Free Quote"}
                </button>
                <p className="text-stone-400 text-xs text-center" style={{ fontFamily: "'Inter', sans-serif" }}>
                  We respond within 24 hours · No obligation
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
